import type { ReactNode } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useStore } from '../../store/store';
import { Footer, type FooterProps } from './Footer';
import { Header } from './Header';
import { MobileFooter, type MobileFooterProps } from './MobileFooter';

export interface ChromeProps {
  /** Current step index (0..3) for the stepper / progress bar. */
  step: number;
  /** Desktop header contextual right slot (help phone / running total / secure badge). */
  headerRight?: ReactNode;
  /** Action for clicking the CURRENT step in the desktop stepper (Clarity:
   *  people on Upload click the "Upload" step expecting the file picker). */
  onActiveStepClick?: () => void;
  /** Tooltip/aria hint for that action ("Add photos"). */
  activeStepTitle?: string;
  /** Steps AHEAD of the current one the screen declares reachable (owner
   *  2026-07-27: "Cart" once the order has prints). */
  stepperForward?: number[];
  /** Handler for those forward clicks — kept SEPARATE from the plain back
   *  navigation so the screen can gate them (Size & Paper routes it through
   *  tryContinue, which still runs the review). */
  onStepperForward?: (step: number) => void;
  /** Desktop footer action bar config. */
  footer?: FooterProps;
  /** Mobile sticky bottom bar config. */
  mobileFooter?: MobileFooterProps;
  children: ReactNode;
}

/**
 * Global chrome wrapper: renders the desktop header + footer around a screen
 * body. Mobile renders NO header at all (owner request: the "STEP N OF 4" row
 * wasted a full row of phone real estate under the theme header) — every
 * post-upload screen instead carries a small in-content back chevron next to
 * its title (pane__back). Screens that need different desktop/mobile bodies
 * read `useIsMobile()` themselves and pass the matching body as children.
 */
export function Chrome({
  step,
  headerRight,
  onActiveStepClick,
  activeStepTitle,
  stepperForward,
  onStepperForward,
  footer,
  mobileFooter,
  children,
}: ChromeProps) {
  const isMobile = useIsMobile();
  const setStep = useStore((s) => s.setStep);
  // Completed steps in the desktop stepper navigate BACK. Steps the screen
  // has explicitly unlocked go FORWARD through its own handler, so validation
  // is never skipped.
  return (
    <div className="app">
      {!isMobile && (
        <Header
          step={step}
          right={headerRight}
          onStepClick={(i) => (i > step && onStepperForward ? onStepperForward(i) : setStep(i))}
          stepperForward={stepperForward}
          onActiveStepClick={onActiveStepClick}
          activeStepTitle={activeStepTitle}
        />
      )}
      <main className="app__main">{children}</main>
      {isMobile
        ? mobileFooter && <MobileFooter {...mobileFooter} />
        : footer && <Footer {...footer} />}
    </div>
  );
}
